import axios from "axios";
import Swal from "sweetalert2";
import { loginConfirmedAction, Logout } from "../store/actions/AuthActions";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";
const LS_KEY = "userDetails";

// ─── Axios header ─────────────────────────────────────────────────────────────

export const setAuthToken = (token) => {
    if (token) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
        delete axios.defaults.headers.common["Authorization"];
    }
};

// ─── JWT helpers ──────────────────────────────────────────────────────────────

const decodeJwtExpiry = (token) => {
    try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload?.exp ? new Date(payload.exp * 1000) : null;
    } catch {
        return null;
    }
};

// ─── API calls ────────────────────────────────────────────────────────────────

export function signUp(name, email, password) {
    return axios.post(`${API_BASE_URL}/api/users/admin/signup`, {
        name,
        email,
        password,
        role: "admin",
    });
}

export function login(email, password) {
    return axios.post(`${API_BASE_URL}/api/users/login`, {
        email,
        password,
        role: "admin",
    });
}

export function formatError(errorResponse) {
    const message = errorResponse?.message || "Something went wrong";
    Swal.fire({ icon: "error", title: "Oops", text: message });
    return message;
}

// ─── localStorage helpers ─────────────────────────────────────────────────────

/**
 * Saves token + user info to localStorage and sets the axios header.
 * Called after a successful login or signup.
 *
 * @param {string} token  - JWT from response.data.token
 * @param {object} user   - user object from response.data.data
 * @returns {object}      - stored payload (used to seed Redux state)
 */
export function saveTokenInLocalStorage(token, user) {
    const expireDate = decodeJwtExpiry(token) || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

    const payload = {
        idToken:    token,
        expireDate: expireDate.toISOString(),
        email:      user?.email  || "",
        name:       user?.name   || "",
        localId:    String(user?._id || user?.id || ""),
        role:       user?.role   || "admin",
    };

    localStorage.setItem(LS_KEY, JSON.stringify(payload));
    setAuthToken(token);
    return payload;
}

/**
 * Removes the token from localStorage and clears the axios header.
 * Called on logout.
 */
export function clearTokenFromLocalStorage() {
    localStorage.removeItem(LS_KEY);
    setAuthToken(null);
}

// ─── Auto-logout timer ────────────────────────────────────────────────────────

export function runLogoutTimer(dispatch, timer, navigate) {
    setTimeout(() => {
        dispatch(Logout(navigate));
    }, Math.max(timer, 0));
}

// ─── Auto-login on app mount ──────────────────────────────────────────────────

export function checkAutoLogin(dispatch, navigate) {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) {
        dispatch(Logout(navigate));
        return;
    }

    let payload;
    try {
        payload = JSON.parse(raw);
    } catch {
        dispatch(Logout(navigate));
        return;
    }

    const expireDate = new Date(payload?.expireDate);

    if (!payload?.idToken || isNaN(expireDate.getTime()) || new Date() > expireDate) {
        dispatch(Logout(navigate));
        return;
    }

    setAuthToken(payload.idToken);
    dispatch(loginConfirmedAction(payload));

    const remainingMs = expireDate.getTime() - Date.now();
    runLogoutTimer(dispatch, remainingMs, navigate);
}
